import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Users, Settings, Activity, Search, Plus, Edit, Trash2, Lock, Unlock,
  Key, ToggleLeft, ToggleRight, UserPlus, Building2, Link2, ScrollText,
  CheckCircle, XCircle, AlertTriangle, Clock, ChevronDown, Eye, RefreshCw,
  Copy, Mail, Upload, Archive, RotateCcw,
} from 'lucide-react';
import ReactivateAccountDialog from '../components/ReactivateAccountDialog';

// ─── Constants ────────────────────────────────────────────────
const ROLES = ['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'ATHLETE'];
const TEAM_ROLES = ['HEAD_SNC', 'SNC', 'HEAD_PHYSIO', 'PHYSIO', 'HEAD_COACH', 'COACH', 'ASSISTANT'];
const CATEGORIES = ['ACADEMY', 'FIRST_TEAM', 'B_TEAM', 'WOMEN', 'OTHER'];
const ROLE_COLORS = {
  ADMIN: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  SNC: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  PHYSIO: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  COACH: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  ATHLETE: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};
const STATUS_ICONS = { active: CheckCircle, inactive: XCircle, locked: Lock };

// ─── Badge ────────────────────────────────────────────────────
const RoleBadge = ({ role }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-wider border ${ROLE_COLORS[role] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}
    data-testid={`role-badge-${role?.toLowerCase()}`}>
    {role}
  </span>
);

const StatusBadge = ({ status }) => {
  const Icon = STATUS_ICONS[status] || Clock;
  const colors = status === 'active' ? 'text-emerald-400' : status === 'locked' ? 'text-amber-400' : 'text-zinc-500';
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${colors}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />{status}
    </span>
  );
};

// ─── Confirmation Dialog ──────────────────────────────────────
const ConfirmDialog = ({ open, onClose, onConfirm, title, description, loading }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
      <DialogHeader>
        <DialogTitle className="text-zinc-100 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />{title}
        </DialogTitle>
        <DialogDescription className="text-zinc-400">{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-700">Cancel</Button>
        <Button onClick={onConfirm} disabled={loading} className="bg-rose-600 hover:bg-rose-500 text-white">
          {loading ? 'Processing...' : 'Confirm'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ═══════════════════════════════════════════════════════════════
//  DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════
const DashboardTab = ({ api, lang }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="text-zinc-500 text-center py-8">Loading dashboard...</div>;
  if (!data) return <div className="text-zinc-500 text-center py-8">Failed to load dashboard</div>;

  const StatCard = ({ label, value, sub, icon: Icon, color = 'text-amber-400' }) => (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color.replace('text-', 'bg-').replace('400', '500/15')}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <div className="text-2xl font-bold text-zinc-100 font-mono">{value}</div>
          <div className="text-xs text-zinc-500">{label}</div>
          {sub && <div className="text-[10px] text-zinc-600">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={data.users.total} sub={`${data.users.active} active`} icon={Users} />
        <StatCard label="Teams" value={data.teams.total} sub={data.teams.inactive > 0 ? `${data.teams.inactive} inactive` : 'All active'} icon={Building2} color="text-blue-400" />
        <StatCard label="Athletes" value={data.athletes.total} sub={`${data.athletes.by_status.RTP || 0} in RTP`} icon={Activity} color="text-emerald-400" />
        <StatCard label="Staff Assignments" value={data.staff_assignments} icon={Link2} color="text-purple-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Role Breakdown */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-100">Users by Role</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {ROLES.map(r => (
              <div key={r} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm border border-zinc-800">
                <RoleBadge role={r} />
                <span className="text-zinc-100 font-mono text-sm">{data.users.by_role[r] || 0}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-100">Recent Activity</CardTitle></CardHeader>
          <CardContent>
            {data.recent_activity.length === 0 ? (
              <div className="text-zinc-600 text-sm text-center py-4">No recent activity</div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {data.recent_activity.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm border border-zinc-800 text-xs">
                    <div>
                      <span className="text-zinc-300 font-medium">{a.user_name}</span>
                      <span className="text-zinc-600 mx-1">&middot;</span>
                      <span className="text-zinc-500">{a.action}</span>
                    </div>
                    <span className="text-zinc-600">{new Date(a.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {data.users.locked > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span className="text-amber-400 text-sm"><strong>{data.users.locked}</strong> user account(s) currently locked</span>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  USERS TAB
// ═══════════════════════════════════════════════════════════════
const UsersTab = ({ api, teams, lang }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [editUser, setEditUser] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Invite form state (replaces direct user creation)
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', role: 'SNC', team_ids: [] });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviting, setInviting] = useState(false);

  // Reactivation (iter_122)
  const [reactivateUser, setReactivateUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter === 'active') params.append('is_active', 'true');
      if (statusFilter === 'inactive') params.append('is_active', 'false');
      if (teamFilter !== 'all') params.append('team_id', teamFilter);
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data);
    } catch (err) { toast.error('Failed to load users'); }
    setLoading(false);
  }, [api, search, roleFilter, statusFilter, teamFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      return toast.error('Name and email are required');
    }
    setInviting(true);
    try {
      const res = await api.post('/users/invite', { ...inviteForm, frontend_origin: window.location.origin });
      setInviteResult(res.data);
      if (res.data.email_sent) {
        toast.success('Invitation email sent!');
      } else {
        toast.warning('Invitation created but email failed. Share the link manually.');
      }
      fetchUsers();
    } catch (err) {
      // iter_122: archived-account reactivation flow
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.error === 'ARCHIVED_ACCOUNT_FOUND') {
        setInviteOpen(false);
        setReactivateUser(detail.archived_user);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to send invitation'));
      }
    }
    setInviting(false);
  };

  const resetInviteDialog = () => {
    setInviteOpen(false);
    setInviteResult(null);
    setInviteForm({ email: '', first_name: '', last_name: '', role: 'SNC', team_ids: [] });
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/users/${editUser.id}`, {
        first_name: editUser.first_name, last_name: editUser.last_name,
        email: editUser.email, role: editUser.role
      });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    setActionLoading(false);
  };

  const handleToggleActive = async (u) => {
    setActionLoading(true);
    try {
      if (u.is_active) {
        // Archive path — same endpoint but iter_122 terminology
        await api.put(`/admin/users/${u.id}/toggle-active`);
        toast.success('User archived');
      } else {
        // Reactivation path — open the shared reactivation dialog (iter_122)
        setConfirmAction(null);
        setActionLoading(false);
        const lastTeamRow = (u.teams || [])[0];
        setReactivateUser({
          id: u.id,
          first_name: u.first_name,
          last_name: u.last_name,
          role: u.role,
          last_team: lastTeamRow ? (lastTeamRow.code || lastTeamRow.name) : null,
          archived_at: u.archived_at || u.updated_at,
        });
        return;
      }
      setConfirmAction(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleResetPassword = async (u) => {
    setActionLoading(true);
    try {
      await api.post('/users/password-reset', { email: u.email, frontend_origin: window.location.origin });
      toast.success(`Password reset email sent to ${u.email}`);
      setConfirmAction(null);
    } catch (err) { toast.error('Failed to send reset email'); }
    setActionLoading(false);
  };

  const handleUnlock = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/unlock`);
      toast.success('User unlocked');
      fetchUsers();
    } catch (err) { toast.error('Failed to unlock'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
              className="bg-zinc-950 border-zinc-800 pl-10" data-testid="user-search" />
          </div>
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-950 border-zinc-800" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-950 border-zinc-800" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-[160px] bg-zinc-950 border-zinc-800" data-testid="user-team-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Teams</SelectItem>
            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setInviteOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="invite-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />Invite User
        </Button>
      </div>

      {/* User Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : users.length === 0 ? (
            <div className="text-zinc-500 text-center py-8">No users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Teams</th>
                    <th className="text-left p-3">Last Login</th>
                    <th className="text-left p-3">Created</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <div className="text-zinc-100 font-medium">{u.first_name} {u.last_name}</div>
                        {u.linked_athlete_name && <div className="text-[10px] text-purple-400">Athlete: {u.linked_athlete_name}</div>}
                      </td>
                      <td className="p-3 text-zinc-400 font-mono text-xs">{u.email}</td>
                      <td className="p-3"><RoleBadge role={u.role} /></td>
                      <td className="p-3"><StatusBadge status={u.account_status} /></td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(u.teams || []).map((t, i) => (
                            <span key={i} className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm border border-zinc-700">{t.code}</span>
                          ))}
                          {(!u.teams || u.teams.length === 0) && <span className="text-zinc-600 text-xs">-</span>}
                        </div>
                      </td>
                      <td className="p-3 text-zinc-500 text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}</td>
                      <td className="p-3 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditUser({ ...u })} className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100" data-testid={`edit-user-${u.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          {u.account_status === 'locked' && (
                            <Button variant="ghost" size="sm" onClick={() => handleUnlock(u)} className="h-7 w-7 p-0 text-amber-400 hover:text-amber-300" data-testid={`unlock-user-${u.id}`}>
                              <Unlock className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm"
                            onClick={() => setConfirmAction({ type: 'reset', user: u })}
                            className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100" data-testid={`reset-pwd-${u.id}`}>
                            <Key className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => setConfirmAction({ type: 'toggle', user: u })}
                            className={`h-7 w-7 p-0 ${u.is_active ? 'text-zinc-400 hover:text-rose-400' : 'text-emerald-400 hover:text-emerald-300'}`}
                            data-testid={`toggle-user-${u.id}`}
                            title={u.is_active ? 'Archive' : 'Reactivate'}>
                            {u.is_active ? <Archive className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite User Dialog (replaces old Create User) */}
      <Dialog open={inviteOpen} onOpenChange={resetInviteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="admin-invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><UserPlus className="w-5 h-5 text-amber-500" />{inviteResult ? 'Invitation Sent' : 'Invite User'}</DialogTitle>
          </DialogHeader>
          {inviteResult ? (
            <div className="space-y-4">
              {inviteResult.email_sent ? (
                <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/20" data-testid="admin-invite-email-sent">
                  <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-emerald-300">Invitation email sent</div>
                    <div className="text-xs text-zinc-400">{inviteResult.invite_email || inviteForm.email}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-amber-300">Email could not be sent</div>
                    <div className="text-xs text-zinc-400">Share the link manually</div>
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-zinc-500 mb-1">Invitation link (backup)</div>
                <div className="flex items-center gap-2">
                  <Input value={inviteResult.invite_link || ''} readOnly className="bg-zinc-950 border-zinc-800 text-xs" data-testid="admin-invite-link" />
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(inviteResult.invite_link || ''); toast.success('Copied!'); }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={resetInviteDialog} className="w-full">Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-zinc-400 text-xs">First Name</Label>
                  <Input value={inviteForm.first_name} onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="admin-invite-firstname" />
                </div>
                <div><Label className="text-zinc-400 text-xs">Last Name</Label>
                  <Input value={inviteForm.last_name} onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="admin-invite-lastname" />
                </div>
              </div>
              <div><Label className="text-zinc-400 text-xs">Email</Label>
                <Input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="admin-invite-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-zinc-400 text-xs">Role</Label>
                  <Select value={inviteForm.role} onValueChange={v => setInviteForm({ ...inviteForm, role: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="admin-invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-400 text-xs">Team(s)</Label>
                  <div className="mt-1 space-y-1 max-h-24 overflow-y-auto border border-zinc-800 rounded p-1.5">
                    {teams.map(tm => (
                      <label key={tm.id} className="flex items-center gap-2 p-1 hover:bg-zinc-800/50 rounded cursor-pointer">
                        <input type="checkbox" checked={inviteForm.team_ids.includes(tm.id)}
                          onChange={e => setInviteForm(f => ({
                            ...f, team_ids: e.target.checked ? [...f.team_ids, tm.id] : f.team_ids.filter(id => id !== tm.id)
                          }))}
                          className="rounded border-zinc-700" data-testid={`admin-invite-team-${tm.id}`} />
                        <span className="text-xs text-zinc-300">{tm.code} — {tm.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-zinc-600">The user will receive an email to set their own password.</p>
              <DialogFooter>
                <Button variant="outline" onClick={resetInviteDialog} className="border-zinc-700">Cancel</Button>
                <Button onClick={handleInviteUser} disabled={inviting} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="admin-send-invite">
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><Edit className="w-5 h-5 text-amber-500" />Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-zinc-400 text-xs">First Name</Label>
                  <Input value={editUser.first_name} onChange={e => setEditUser({ ...editUser, first_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="edit-user-firstname" />
                </div>
                <div><Label className="text-zinc-400 text-xs">Last Name</Label>
                  <Input value={editUser.last_name} onChange={e => setEditUser({ ...editUser, last_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="edit-user-lastname" />
                </div>
              </div>
              <div><Label className="text-zinc-400 text-xs">Email</Label>
                <Input type="email" value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="edit-user-email" />
              </div>
              <div><Label className="text-zinc-400 text-xs">Role</Label>
                <Select value={editUser.role} onValueChange={v => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleEditUser} disabled={actionLoading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="confirm-edit-user">
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      {confirmAction && (
        <ConfirmDialog
          open={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          title={confirmAction.type === 'toggle' ? (confirmAction.user.is_active ? 'Archive this account?' : 'Reactivate account') : 'Reset Password'}
          description={confirmAction.type === 'toggle'
            ? (confirmAction.user.is_active
              ? `The user will no longer be able to sign in and will be removed from the active lists. All historical data will be preserved and the account can be reactivated later.`
              : `Open the reactivation flow for ${confirmAction.user.first_name} ${confirmAction.user.last_name}? Their previous history will be preserved.`)
            : `Send a password reset email to ${confirmAction.user.email}? They will receive a link to set a new password.`}
          onConfirm={() => confirmAction.type === 'toggle' ? handleToggleActive(confirmAction.user) : handleResetPassword(confirmAction.user)}
          loading={actionLoading}
        />
      )}

      {/* Reactivate Account Dialog (iter_122) */}
      <ReactivateAccountDialog
        open={!!reactivateUser}
        onClose={() => setReactivateUser(null)}
        archivedUser={reactivateUser}
        teams={teams}
        api={api}
        lang={lang}
        onReactivated={() => { fetchUsers(); }}
      />

    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  TEAMS TAB
// ═══════════════════════════════════════════════════════════════
const TeamsTab = ({ api, lang }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', code: '', age_group: '', category: 'ACADEMY', season: '2025-26' });

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/teams');
      setTeams(res.data);
    } catch (err) { toast.error('Failed to load teams'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const handleCreateTeam = async () => {
    if (!newTeam.name || !newTeam.code || !newTeam.age_group) return toast.error('Name, code, and age group required');
    setActionLoading(true);
    try {
      await api.post('/teams', newTeam);
      toast.success('Team created');
      setCreateOpen(false);
      setNewTeam({ name: '', code: '', age_group: '', category: 'ACADEMY', season: '2025-26' });
      fetchTeams();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleEditTeam = async () => {
    if (!editTeam) return;
    setActionLoading(true);
    try {
      await api.put(`/teams/${editTeam.id}`, {
        name: editTeam.name, age_group: editTeam.age_group, category: editTeam.category, season: editTeam.season, description: editTeam.description
      });
      toast.success('Team updated');
      setEditTeam(null);
      fetchTeams();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleToggleActive = async (t) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/teams/${t.id}/toggle-active`);
      toast.success(t.is_active ? 'Team deactivated' : 'Team activated');
      setConfirmAction(null);
      fetchTeams();
    } catch (err) { toast.error('Failed'); }
    setActionLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-teams">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="create-team-btn">
          <Plus className="w-4 h-4 mr-2" />New Team
        </Button>
      </div>

      {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(t => (
            <Card key={t.id} className={`border-zinc-800 ${t.is_active ? 'bg-zinc-900/50' : 'bg-zinc-900/20 opacity-60'}`} data-testid={`team-card-${t.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-amber-500" />{t.code}
                    {!t.is_active && <span className="text-[10px] bg-rose-500/20 text-rose-400 px-1.5 py-0.5 rounded-sm">Inactive</span>}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditTeam({ ...t })} className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100" data-testid={`edit-team-${t.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmAction({ team: t })}
                      className={`h-7 w-7 p-0 ${t.is_active ? 'text-zinc-400 hover:text-rose-400' : 'text-emerald-400'}`} data-testid={`toggle-team-${t.id}`}>
                      {t.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="text-zinc-300">{t.name}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-zinc-500">
                  <span>Age: <span className="text-zinc-300">{t.age_group}</span></span>
                  <span>Season: <span className="text-zinc-300">{t.season}</span></span>
                  <span>Category: <span className="text-zinc-300">{t.category}</span></span>
                  <span>GPS: <span className={`font-bold ${t.gps_mode === 'GPS' ? 'text-emerald-400' : 'text-zinc-400'}`}>{t.gps_mode}</span></span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-zinc-800">
                  <span className="text-zinc-500"><Users className="w-3 h-3 inline mr-1" />{t.athlete_count} athletes</span>
                  <span className="text-zinc-500">{t.staff?.length || 0} staff</span>
                </div>
                {t.staff?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {t.staff.map((s, i) => (
                      <span key={i} className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm border border-zinc-700">
                        {s.name.split(' ').pop()} ({s.role_in_team})
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-500" />Create Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-zinc-400 text-xs">Code</Label>
                <Input value={newTeam.code} onChange={e => setNewTeam({ ...newTeam, code: e.target.value })} placeholder="U20" className="bg-zinc-950 border-zinc-800" data-testid="new-team-code" />
              </div>
              <div><Label className="text-zinc-400 text-xs">Age Group</Label>
                <Input value={newTeam.age_group} onChange={e => setNewTeam({ ...newTeam, age_group: e.target.value })} placeholder="Under 20" className="bg-zinc-950 border-zinc-800" data-testid="new-team-agegroup" />
              </div>
            </div>
            <div><Label className="text-zinc-400 text-xs">Name</Label>
              <Input value={newTeam.name} onChange={e => setNewTeam({ ...newTeam, name: e.target.value })} placeholder="Under 20" className="bg-zinc-950 border-zinc-800" data-testid="new-team-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-zinc-400 text-xs">Category</Label>
                <Select value={newTeam.category} onValueChange={v => setNewTeam({ ...newTeam, category: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400 text-xs">Season</Label>
                <Input value={newTeam.season} onChange={e => setNewTeam({ ...newTeam, season: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleCreateTeam} disabled={actionLoading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="confirm-create-team">
              {actionLoading ? 'Creating...' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={!!editTeam} onOpenChange={() => setEditTeam(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><Edit className="w-5 h-5 text-amber-500" />Edit Team</DialogTitle>
          </DialogHeader>
          {editTeam && (
            <div className="space-y-3">
              <div><Label className="text-zinc-400 text-xs">Name</Label>
                <Input value={editTeam.name} onChange={e => setEditTeam({ ...editTeam, name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="edit-team-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-zinc-400 text-xs">Age Group</Label>
                  <Input value={editTeam.age_group} onChange={e => setEditTeam({ ...editTeam, age_group: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
                <div><Label className="text-zinc-400 text-xs">Season</Label>
                  <Input value={editTeam.season} onChange={e => setEditTeam({ ...editTeam, season: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                </div>
              </div>
              <div><Label className="text-zinc-400 text-xs">Category</Label>
                <Select value={editTeam.category} onValueChange={v => setEditTeam({ ...editTeam, category: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTeam(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleEditTeam} disabled={actionLoading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="confirm-edit-team">
              {actionLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmAction && (
        <ConfirmDialog open={!!confirmAction} onClose={() => setConfirmAction(null)}
          title={confirmAction.team?.is_active ? 'Deactivate Team' : 'Activate Team'}
          description={`${confirmAction.team?.is_active ? 'Deactivate' : 'Activate'} team ${confirmAction.team?.name}?`}
          onConfirm={() => handleToggleActive(confirmAction.team)} loading={actionLoading} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ASSIGNMENTS TAB
// ═══════════════════════════════════════════════════════════════
const AssignmentsTab = ({ api, teams, lang }) => {
  const [assignments, setAssignments] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [newAssign, setNewAssign] = useState({ user_id: '', team_id: '', role_in_team: 'SNC' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assignRes, staffRes] = await Promise.all([
        api.get('/admin/staff-teams'), api.get('/users/staff')
      ]);
      setAssignments(assignRes.data);
      setStaffUsers(staffRes.data);
    } catch (err) { toast.error('Failed to load'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!newAssign.user_id || !newAssign.team_id) return toast.error('Select staff and team');
    setActionLoading(true);
    try {
      await api.post('/staff-teams', newAssign);
      toast.success('Assignment created');
      setAddOpen(false);
      setNewAssign({ user_id: '', team_id: '', role_in_team: 'SNC' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleEditRole = async () => {
    if (!editAssignment) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/staff-teams/${editAssignment.id}`, { role_in_team: editAssignment.role_in_team });
      toast.success('Role updated');
      setEditAssignment(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleRemove = async (a) => {
    setActionLoading(true);
    try {
      await api.delete(`/staff-teams/${a.id}`);
      toast.success('Assignment removed');
      setConfirmRemove(null);
      fetchData();
    } catch (err) { toast.error('Failed'); }
    setActionLoading(false);
  };

  // Group by staff
  const grouped = {};
  assignments.forEach(a => {
    if (!grouped[a.user_id]) {
      grouped[a.user_id] = { name: a.user_name, email: a.user_email, role: a.user_role, teams: [] };
    }
    grouped[a.user_id].teams.push(a);
  });

  return (
    <div className="space-y-4" data-testid="admin-assignments">
      <div className="flex justify-end">
        <Button onClick={() => setAddOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="add-assignment-btn">
          <Plus className="w-4 h-4 mr-2" />Add Assignment
        </Button>
      </div>

      {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : Object.keys(grouped).length === 0 ? (
        <div className="text-zinc-500 text-center py-8">No assignments found</div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([userId, staff]) => (
            <Card key={userId} className="bg-zinc-900/50 border-zinc-800" data-testid={`staff-assignments-${userId}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-zinc-100 flex items-center gap-2">
                    {staff.name}
                    <RoleBadge role={staff.role} />
                    <span className="text-zinc-600 text-xs font-normal">{staff.email}</span>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-500 uppercase tracking-wider">
                        <th className="text-left p-2">Team</th>
                        <th className="text-left p-2">Role in Team</th>
                        <th className="text-left p-2">Since</th>
                        <th className="text-right p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.teams.map(a => (
                        <tr key={a.id} className="border-b border-zinc-800/50">
                          <td className="p-2 text-zinc-100">{a.team_code} - {a.team_name}</td>
                          <td className="p-2">
                            <span className="bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-sm border border-zinc-700">{a.role_in_team}</span>
                          </td>
                          <td className="p-2 text-zinc-500">{a.start_date}</td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setEditAssignment({ ...a })} className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100" data-testid={`edit-assign-${a.id}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(a)} className="h-7 w-7 p-0 text-zinc-400 hover:text-rose-400" data-testid={`remove-assign-${a.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2"><Link2 className="w-5 h-5 text-amber-500" />Add Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-400 text-xs">Staff Member</Label>
              <Select value={newAssign.user_id} onValueChange={v => setNewAssign({ ...newAssign, user_id: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-staff-select"><SelectValue placeholder="Select staff..." /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {staffUsers.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-400 text-xs">Team</Label>
              <Select value={newAssign.team_id} onValueChange={v => setNewAssign({ ...newAssign, team_id: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-team-select"><SelectValue placeholder="Select team..." /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.code} - {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-400 text-xs">Role in Team</Label>
              <Select value={newAssign.role_in_team} onValueChange={v => setNewAssign({ ...newAssign, role_in_team: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="assign-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {TEAM_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAdd} disabled={actionLoading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="confirm-add-assignment">
              {actionLoading ? 'Adding...' : 'Add Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editAssignment} onOpenChange={() => setEditAssignment(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Assignment Role</DialogTitle>
          </DialogHeader>
          {editAssignment && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-400">
                <strong className="text-zinc-100">{editAssignment.user_name}</strong> in <strong className="text-zinc-100">{editAssignment.team_name}</strong>
              </div>
              <div><Label className="text-zinc-400 text-xs">Role in Team</Label>
                <Select value={editAssignment.role_in_team} onValueChange={v => setEditAssignment({ ...editAssignment, role_in_team: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {TEAM_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAssignment(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleEditRole} disabled={actionLoading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="confirm-edit-assignment">
              {actionLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmRemove && (
        <ConfirmDialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)}
          title="Remove Assignment"
          description={`Remove ${confirmRemove.user_name} from ${confirmRemove.team_name}?`}
          onConfirm={() => handleRemove(confirmRemove)} loading={actionLoading} />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  SETTINGS TAB
// ═══════════════════════════════════════════════════════════════
const SettingsTab = ({ api, lang }) => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState({});

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/settings');
      const vals = {};
      res.data.forEach(s => { vals[s.key] = s.value; });
      setEditValues(vals);
      // Also get org logo
      const meRes = await api.get('/auth/me');
      res.data._orgLogoUrl = meRes.data.organization_logo;
      setSettings(res.data);
    } catch (err) { toast.error('Failed to load settings'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async (key) => {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await api.put(`/admin/settings/${key}`, { value: editValues[key] });
      toast.success(`Setting "${key}" updated`);
      fetchSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(p => ({ ...p, [key]: false }));
  };

  const categories = [...new Set(settings.map(s => s.category))];

  if (loading) return <div className="text-zinc-500 text-center py-8">Loading settings...</div>;

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Organization Logo Upload */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-100 uppercase tracking-wider">Organization Logo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden border border-zinc-700" data-testid="org-logo-preview">
              {(() => {
                const logoUrl = settings._orgLogoUrl;
                if (logoUrl) return <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />;
                return <Building2 className="w-6 h-6 text-zinc-500" />;
              })()}
            </div>
            <div className="flex-1">
              <p className="text-xs text-zinc-500 mb-2">
                Upload your organization logo. PNG, JPG, JPEG, WebP, SVG — max 5 MB. Aspect ratio is preserved; the PDF engine constrains the rendered logo to 20 mm wide in the report header.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-cyan-600 hover:bg-cyan-700 text-white rounded cursor-pointer" data-testid="org-logo-upload-label">
                  <Upload className="w-3.5 h-3.5" />
                  {settings._orgLogoUrl ? 'Replace Logo' : 'Upload Logo'}
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden"
                    data-testid="org-logo-upload"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File too large (max 5 MB)');
                        e.target.value = '';
                        return;
                      }
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const res = await api.post('/admin/organization/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        toast.success('Logo uploaded');
                        setSettings(prev => ({ ...prev, _orgLogoUrl: res.data.logo_url }));
                      } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                      e.target.value = '';
                    }}
                  />
                </label>
                {settings._orgLogoUrl && (
                  <Button
                    variant="outline" size="sm"
                    className="border-red-500/40 text-red-300 hover:bg-red-900/20 h-8 text-xs"
                    data-testid="org-logo-remove-btn"
                    onClick={async () => {
                      try {
                        await api.delete('/admin/organization/logo');
                        toast.success('Logo removed');
                        setSettings(prev => ({ ...prev, _orgLogoUrl: null }));
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Remove failed');
                      }
                    }}
                  >
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {categories.map(cat => (
        <Card key={cat} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-100 uppercase tracking-wider">{cat}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {settings.filter(s => s.category === cat).map(s => (
              <div key={s.key} className="flex items-center gap-4 p-3 bg-zinc-950/50 rounded-sm border border-zinc-800" data-testid={`setting-${s.key}`}>
                <div className="flex-1">
                  <div className="text-sm text-zinc-100 font-medium">{s.key}</div>
                  <div className="text-xs text-zinc-500">{s.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  {typeof s.value === 'boolean' ? (
                    <Button variant="outline" size="sm"
                      onClick={() => setEditValues(p => ({ ...p, [s.key]: !p[s.key] }))}
                      className={`h-8 ${editValues[s.key] ? 'border-emerald-500/50 text-emerald-400' : 'border-zinc-700 text-zinc-400'}`}>
                      {editValues[s.key] ? <ToggleRight className="w-4 h-4 mr-1" /> : <ToggleLeft className="w-4 h-4 mr-1" />}
                      {editValues[s.key] ? 'ON' : 'OFF'}
                    </Button>
                  ) : (
                    <Input value={editValues[s.key] ?? ''} onChange={e => setEditValues(p => ({ ...p, [s.key]: typeof s.value === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                      className="bg-zinc-950 border-zinc-800 w-28 h-8 text-sm" data-testid={`setting-input-${s.key}`} />
                  )}
                  {editValues[s.key] !== s.value && (
                    <Button size="sm" onClick={() => handleSave(s.key)} disabled={saving[s.key]}
                      className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-8 text-xs" data-testid={`save-setting-${s.key}`}>
                      {saving[s.key] ? '...' : 'Save'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  AUDIT TAB
// ═══════════════════════════════════════════════════════════════
const AuditTab = ({ api, lang }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [successFilter, setSuccessFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (moduleFilter !== 'all') params.append('module', moduleFilter);
      if (successFilter !== 'all') params.append('success', successFilter === 'success');
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('limit', '100');
      const res = await api.get(`/admin/audit?${params}`);
      setLogs(res.data);
    } catch (err) { toast.error('Failed to load audit logs'); }
    setLoading(false);
  }, [api, moduleFilter, successFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4" data-testid="admin-audit">
      <div className="flex flex-wrap gap-3 items-end">
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-950 border-zinc-800" data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Modules</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="auth">Auth</SelectItem>
          </SelectContent>
        </Select>
        <Select value={successFilter} onValueChange={setSuccessFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-950 border-zinc-800" data-testid="audit-success-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Results</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <Label className="text-zinc-400 text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-zinc-950 border-zinc-800 w-[150px]" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-zinc-950 border-zinc-800 w-[150px]" />
        </div>
        <Button variant="outline" onClick={fetchLogs} className="border-zinc-700 h-9" data-testid="refresh-audit">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : logs.length === 0 ? (
            <div className="text-zinc-500 text-center py-8">No audit logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500 uppercase tracking-wider">
                    <th className="text-left p-3">Timestamp</th>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Module</th>
                    <th className="text-left p-3">Action</th>
                    <th className="text-left p-3">Entity</th>
                    <th className="text-left p-3">Details</th>
                    <th className="text-left p-3">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => (
                    <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                      <td className="p-3 text-zinc-500 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="p-3 text-zinc-100">{l.user_name}</td>
                      <td className="p-3"><span className="bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm">{l.module}</span></td>
                      <td className="p-3 text-zinc-300">{l.action}</td>
                      <td className="p-3 text-zinc-500">{l.entity_type}{l.entity_id ? ` (${l.entity_id.slice(0, 8)}...)` : ''}</td>
                      <td className="p-3 text-zinc-500 max-w-[200px] truncate">{l.details}</td>
                      <td className="p-3">{l.success ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════
const AdminPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  return (
    <div className="space-y-6 fade-in" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
          <Shield className="w-6 h-6 text-amber-500" />
          {language === 'IT' ? 'Pannello Amministratore' : 'Admin Panel'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{language === 'IT' ? 'Gestione utenti, squadre e impostazioni' : 'Manage users, teams, and system settings'}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900/50 border border-zinc-800 flex-wrap">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-dashboard">
            <Activity className="w-4 h-4 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-users">
            <Users className="w-4 h-4 mr-2" />Users
          </TabsTrigger>
          <TabsTrigger value="teams" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-teams">
            <Building2 className="w-4 h-4 mr-2" />Teams
          </TabsTrigger>
          <TabsTrigger value="assignments" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-assignments">
            <Link2 className="w-4 h-4 mr-2" />Assignments
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-settings">
            <Settings className="w-4 h-4 mr-2" />Settings
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-zinc-800" data-testid="admin-tab-audit">
            <ScrollText className="w-4 h-4 mr-2" />Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4"><DashboardTab api={api} lang={language} /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab api={api} teams={teams} lang={language} /></TabsContent>
        <TabsContent value="teams" className="mt-4"><TeamsTab api={api} lang={language} /></TabsContent>
        <TabsContent value="assignments" className="mt-4"><AssignmentsTab api={api} teams={teams} lang={language} /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsTab api={api} lang={language} /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab api={api} lang={language} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
